import React from "react";
import styled from "styled-components";
import { Spacer } from "../Spacer";
import { Text } from "../Text";
import { mediaQueries, style } from "../style";
import { bigWarning } from "../utils/warning";
import { CreatePageV2MainSection } from "./CreatePageV2MainSection";
import { CreatePageV2OptionalSection } from "./CreatePageV2OptionalSection";
import { CreatePageV2ItemizedSummary, CreatePageV2ResourceSummary } from "./CreatePageV2Summary";

type Props = {
  /**
   * The title of the page. Placed in an h1 element.
   */
  title: string;
  /**
   * The position to place the optional section on mobile. It can be placed either above or below the main section.
   * @default "top"
   */
  mobileOptionalPosition?: "top" | "bottom";
  /**
   * The margin to apply to the sticky summary element. In CSS this value must be global to the page, so we need to be able to
   * set it here. 78px is the default value because that is the height of the global header.
   * @default "78px"
   */
  UNSAFE_globalStickyMargin?: string;
};

const isHalfScreenMediaQuery = style.mediaQueries.largeMax;

export const Container = styled.div`
  margin-left: auto;
  margin-right: auto;

  max-width: calc(1000px + ${style.vars.space[8]});

  ${isHalfScreenMediaQuery} {
    max-width: 600px;
  }
`;

const PageTitle = styled(Text).attrs({ type: "h1" })`
  margin-bottom: 0;
`;

const InnerContainer = styled.div`
  all: unset;

  ${mediaQueries.smallMax} {
    margin: ${style.vars.space[5]};
  }
`;

const LayoutContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  column-gap: ${style.vars.space[8]};
`;

const MainWrapper = styled.div`
  all: unset;
  flex-basis: 60%;
  flex-grow: 3;

  display: flex;
  row-gap: ${style.vars.space[8]};
  flex-direction: column;

  max-width: min(100%, 600px);
`;

const OptionalWrapper = styled.div`
  ${mediaQueries.xlargeMin} {
    display: none;
  }
`;

const AsideWrapper = styled.div`
  all: unset;
  max-width: 400px;
  flex-basis: 40%;
  flex-shrink: 2;

  ${isHalfScreenMediaQuery} {
    display: none;
  }
`;

const MobileSummaryBuffer = styled.div`
  all: unset;
  display: block;
  height: 200px;
`;

const Sticky = styled.div<{ $additionalTopMargin: string }>`
  all: unset;
  position: sticky;
  top: calc(${style.vars.space[3]} + ${(props) => props.$additionalTopMargin});
  max-height: calc(100vh - ${style.vars.space[3]} * 2 - ${(props) => props.$additionalTopMargin});
`;

const InheritMaxHeight = styled.div`
  max-height: inherit;
`;

export const CreatePageV2Body: React.FC<Props> = (props) => {
  const { title, mobileOptionalPosition = "top", UNSAFE_globalStickyMargin = "78px" } = props;

  const isMobile = isHalfScreenMediaQuery.useMatchesBreakpoint();

  /**
   * Iterate through all children in order to pluck out the main, summary and optional sections.
   * Additionally, we want to warn if any children are unexpected.
   */
  let main: React.ReactNode;
  let summary: React.ReactNode;
  let optional: React.ReactNode;

  React.Children.forEach(props.children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === CreatePageV2MainSection) {
        if (main && process.env.NODE_ENV !== "production") {
          bigWarning(
            `CreatePageV2.Main used multiple times on CreatePageV2.Body. Only one Main section is allowed at once. The second element is ignored.`,
            "CreatePageV2Body-TooManyMains"
          );
          return;
        }

        main = child;
        return;
      } else if (child.type === CreatePageV2ResourceSummary || child.type === CreatePageV2ItemizedSummary) {
        if (summary && process.env.NODE_ENV !== "production") {
          bigWarning(
            `CreatePageV2.(ResourceSummary|ItemizedSummary) used multiple times on CreatePageV2.Body. Only one (ResourceSummary|ItemizedSummary) section is allowed at once. The second element is ignored.`,
            "CreatePageV2Body-TooManySummaries"
          );
          return;
        }

        summary = child;
      } else if (child.type === CreatePageV2OptionalSection) {
        if (optional && process.env.NODE_ENV !== "production") {
          bigWarning(
            `CreatePageV2.Optional used multiple times on CreatePageV2.Body. Only one Optional section is allowed at once. The second element is ignored.`,
            "CreatePageV2Body-TooManyOptionals"
          );
          return;
        }

        optional = child;
      } else if (process.env.NODE_ENV !== "production") {
        bigWarning(
          `CreatePageV2.Body expects children to be CreatePageV2.Main, CreatePageV2.Billing, CreatePageV2.Optional and nothing else, but included a "${child.type}". This element is ignored.`,
          "CreatePageV2Body-UnknownReactElements"
        );
      }
    } else if (process.env.NODE_ENV !== "production") {
      bigWarning(
        `CreatePageV2.Body expects children to be CreatePageV2.Main, CreatePageV2.Billing, CreatePageV2.Optional and nothing else, but included a "${typeof child}". This element is ignored.`,
        "CreatePageV2Body-UnknownChild"
      );
    }
  });

  if ((!main || !summary) && process.env.NODE_ENV !== "production") {
    bigWarning(`CreatePageV2.Body expected a CreatePageV2.Main and CreatePageV2.Billing as direct children.`, "CreatePageV2Body-NoMain");
  }

  const mainContent = [<div key="main">{main}</div>];
  const asideContent = [<InheritMaxHeight key="summary">{summary}</InheritMaxHeight>];

  if (isMobile) {
    mainContent.push(<MobileSummaryBuffer key="buffer" />, ...asideContent);
  }

  if (optional) {
    if (isMobile) {
      if (mobileOptionalPosition === "top") {
        mainContent.unshift(<OptionalWrapper key="optional">{optional}</OptionalWrapper>);
      } else {
        mainContent.push(<OptionalWrapper key="optional">{optional}</OptionalWrapper>);
      }
    } else {
      asideContent.push(<Spacer key="spacer" space={3} />, <div key="optional">{optional}</div>);
    }
  }

  return (
    <Container>
      <InnerContainer>
        <LayoutContainer>
          <MainWrapper>
            <PageTitle>{title}</PageTitle>
            {mainContent}
          </MainWrapper>
          <AsideWrapper>
            <Sticky $additionalTopMargin={UNSAFE_globalStickyMargin}>{asideContent}</Sticky>
          </AsideWrapper>
        </LayoutContainer>
      </InnerContainer>
    </Container>
  );
};

CreatePageV2Body.displayName = "CreatePageV2Body";
